<template>
  <div v-if="show" class="exercise-picker-overlay" @click="closeModal">
    <div class="exercise-picker-modal" @click.stop>
      <div class="modal-header">
        <h3>Select Exercises</h3>
        <button class="btn-close" @click="closeModal">&times;</button>
      </div>

      <div class="modal-body">
        <!-- Filters -->
        <div class="filter-bar">
          <div class="search-box">
            <i class="bi bi-search"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search exercises..."
              class="form-control"
            />
          </div>

          <div class="filter-group">
            <select v-model="selectedCategory" class="form-select">
              <option value="">All Categories</option>
              <option v-for="cat in categories" :key="cat" :value="cat">
                {{ formatCategory(cat) }}
              </option>
            </select>

            <select v-model="selectedDifficulty" class="form-select">
              <option value="">All Difficulty</option>
              <option value="easy">Easy</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>

        <!-- Selected exercises count -->
        <div class="selection-info">
          <span class="selected-count">
            <i class="bi bi-check-circle-fill"></i>
            {{ selectedExerciseIds.length }} exercise{{ selectedExerciseIds.length !== 1 ? 's' : '' }} selected
          </span>
          <button
            v-if="selectedExerciseIds.length > 0"
            class="btn btn-link btn-sm"
            @click="clearSelection"
          >
            Clear all
          </button>
        </div>

        <!-- Exercise list -->
        <div class="exercise-list">
          <div v-if="loading" class="loading-state">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else-if="filteredExercises.length === 0" class="empty-state">
            <i class="bi bi-search"></i>
            <p>No exercises found</p>
          </div>

          <div
            v-for="exercise in filteredExercises"
            :key="exercise.id"
            class="exercise-item"
            :class="{ selected: isSelected(exercise.id) }"
            @click="toggleExercise(exercise)"
          >
            <div class="exercise-checkbox">
              <i :class="isSelected(exercise.id) ? 'bi bi-check-square-fill' : 'bi bi-square'"></i>
            </div>

            <div class="exercise-info">
              <div class="exercise-name">{{ exercise.exercise_name }}</div>
              <div class="exercise-meta">
                <span class="badge badge-category">{{ formatCategory(exercise.category) }}</span>
                <span :class="['badge', `badge-${exercise.difficulty}`]">
                  {{ exercise.difficulty }}
                </span>
                <span v-if="exercise.sets_reps" class="sets-reps">
                  <i class="bi bi-repeat"></i> {{ exercise.sets_reps }}
                </span>
                <span v-if="exercise.duration" class="duration">
                  <i class="bi bi-clock"></i> {{ exercise.duration }}
                </span>
              </div>
              <div v-if="exercise.description" class="exercise-desc">
                {{ truncateText(exercise.description, 100) }}
              </div>
            </div>

            <div v-if="exercise.video_url || exercise.video_file_path" class="exercise-video-indicator">
              <i class="bi bi-play-circle"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">Cancel</button>
        <button class="btn btn-primary" @click="confirmSelection">
          <i class="bi bi-check-lg"></i>
          Add {{ selectedExerciseIds.length }} Exercise{{ selectedExerciseIds.length !== 1 ? 's' : '' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { RTPService } from "@/services/rtp-service";

export default {
  name: "ExercisePicker",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    initialSelected: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'select'],
  data() {
    return {
      exercises: [],
      selectedExerciseIds: [],
      searchQuery: "",
      selectedCategory: "",
      selectedDifficulty: "",
      loading: false,
      categories: [
        'strength', 'conditioning', 'technical', 'tactical',
        'mobility', 'recovery', 'warmup', 'aerobic',
        'agility', 'balance', 'sport_specific', 'contact', 'mental', 'gym'
      ]
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    clubId() {
      return this.getProfile?.clubId || "";
    },
    filteredExercises() {
      let result = this.exercises;

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(ex =>
          ex.exercise_name?.toLowerCase().includes(query) ||
          ex.description?.toLowerCase().includes(query)
        );
      }

      // Filter by category
      if (this.selectedCategory) {
        result = result.filter(ex => ex.category === this.selectedCategory);
      }

      // Filter by difficulty
      if (this.selectedDifficulty) {
        result = result.filter(ex => ex.difficulty === this.selectedDifficulty);
      }

      return result;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadExercises();
        this.selectedExerciseIds = [...this.initialSelected];
      }
    },
    initialSelected: {
      handler(newVal) {
        this.selectedExerciseIds = [...newVal];
      },
      immediate: true
    }
  },
  methods: {
    async loadExercises() {
      this.loading = true;
      try {
        const response = await RTPService.getExercises({
          clubId: this.clubId,
          action: "getExercises"
        });

        if (response.data) {
          // Only show active exercises
          this.exercises = response.data.filter(ex => ex.is_active !== 0 && ex.is_active !== '0');
        }
      } catch (error) {
        console.error("Error loading exercises:", error);
      } finally {
        this.loading = false;
      }
    },

    isSelected(id) {
      return this.selectedExerciseIds.includes(parseInt(id));
    },

    toggleExercise(exercise) {
      const id = parseInt(exercise.id);
      const index = this.selectedExerciseIds.indexOf(id);

      if (index === -1) {
        this.selectedExerciseIds.push(id);
      } else {
        this.selectedExerciseIds.splice(index, 1);
      }
    },

    clearSelection() {
      this.selectedExerciseIds = [];
    },

    closeModal() {
      this.$emit('close');
    },

    confirmSelection() {
      // Get full exercise objects for selected IDs
      const selectedExercises = this.exercises.filter(ex =>
        this.selectedExerciseIds.includes(parseInt(ex.id))
      );

      this.$emit('select', {
        ids: this.selectedExerciseIds,
        exercises: selectedExercises
      });
      this.closeModal();
    },

    formatCategory(category) {
      if (!category) return "";
      return category
        .replace(/_/g, " ")
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    }
  }
};
</script>

<style lang="scss" scoped>
.exercise-picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.exercise-picker-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;

    &:hover {
      color: #333;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
}

.filter-bar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  .search-box {
    flex: 1;
    min-width: 200px;
    position: relative;

    i {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
    }

    input {
      padding-left: 38px;
    }
  }

  .filter-group {
    display: flex;
    gap: 0.5rem;

    select {
      min-width: 140px;
    }
  }
}

.selection-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;

  .selected-count {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    color: #495057;

    i {
      color: #28a745;
    }
  }

  .btn-link {
    color: #dc3545;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.exercise-list {
  max-height: 400px;
  overflow-y: auto;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #999;

  i {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
  }
}

.exercise-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #eee;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    background: #f8f9ff;
  }

  &.selected {
    border-color: #28a745;
    background: #f0fff4;

    .exercise-checkbox i {
      color: #28a745;
    }
  }

  .exercise-checkbox {
    font-size: 1.25rem;
    color: #ccc;
    flex-shrink: 0;
  }

  .exercise-info {
    flex: 1;
    min-width: 0;

    .exercise-name {
      font-weight: 600;
      font-size: 1rem;
      margin-bottom: 0.25rem;
    }

    .exercise-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 0.5rem;

      .badge {
        font-size: 0.7rem;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-weight: 500;
      }

      .badge-category {
        background: #e9ecef;
        color: #495057;
      }

      .badge-easy {
        background: #d4edda;
        color: #155724;
      }

      .badge-intermediate {
        background: #fff3cd;
        color: #856404;
      }

      .badge-advanced {
        background: #f8d7da;
        color: #721c24;
      }

      .sets-reps,
      .duration {
        font-size: 0.75rem;
        color: #666;
        display: flex;
        align-items: center;
        gap: 0.25rem;

        i {
          font-size: 0.7rem;
        }
      }
    }

    .exercise-desc {
      font-size: 0.85rem;
      color: #666;
      line-height: 1.4;
    }
  }

  .exercise-video-indicator {
    color: #007bff;
    font-size: 1.25rem;
    flex-shrink: 0;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;

  .btn {
    padding: 0.5rem 1.25rem;
    border-radius: 6px;
    font-weight: 500;

    i {
      margin-right: 0.5rem;
    }
  }

  .btn-secondary {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    color: #495057;

    &:hover {
      background: #e9ecef;
    }
  }

  .btn-primary {
    background: #007bff;
    border: none;
    color: white;

    &:hover {
      background: #0056b3;
    }
  }
}

@media (max-width: 576px) {
  .exercise-picker-modal {
    width: 95%;
    max-height: 90vh;
  }

  .filter-bar {
    flex-direction: column;

    .filter-group {
      width: 100%;

      select {
        flex: 1;
      }
    }
  }
}
</style>
